import { PAGE_NAVIGATION, PagePath } from '@pokerroom/types';

export const MEMBER_WALLET_PATH = '/admin/points-config/wallet';

export const WALLET_DESTINATIONS = [
  { section: 'vouchers', label: 'nav.walletVouchers', iconKey: 'walletVouchers', icon: 'ticket' },
  { section: 'passes', label: 'nav.walletPasses', iconKey: 'walletPasses', icon: 'id' },
  {
    section: 'monthly',
    label: 'nav.walletMonthly',
    iconKey: 'walletMonthly',
    icon: 'calendar-user',
  },
] as const;

export const isMemberWalletPath = (path: string) =>
  path === MEMBER_WALLET_PATH ||
  WALLET_DESTINATIONS.some(({ section }) => path === `${MEMBER_WALLET_PATH}/${section}`);

// The wallet view retains the existing settings page grant; it is not a new catalog key.
export const memberSettingsPermissionPath = (path: string) =>
  isMemberWalletPath(path) ? '/admin/points-config' : path;

export interface NavItem {
  label: string;
  iconKey: string;
  icon: string;
  path?: string;
  children?: { label: string; path: string; iconKey: string; icon: string; section?: string }[];
  requiredRoles?: string[];
}

export function buildNavItems(
  t: (key: string) => string,
  iconOverrides?: Record<string, string>,
): NavItem[] {
  const items: NavItem[] = PAGE_NAVIGATION.map((entry) => ({
    label: t(entry.labelKey),
    iconKey: entry.iconKey,
    icon: iconOverrides?.[entry.iconKey] || entry.defaultIcon,
    ...(entry.pagePath
      ? { path: entry.pagePath }
      : {
          children: entry.children!.map((child) => ({
            label: t(child.labelKey),
            path: child.pagePath,
            iconKey: child.iconKey,
            icon: iconOverrides?.[child.iconKey] || child.defaultIcon,
          })),
        }),
  }));
  // Keep catalog paths, permissions and child icon overrides intact while separating settings.
  const takeChildren = (groupKey: string, paths: string[]) => {
    const group = items.find((item) => item.iconKey === groupKey);
    const selected = group?.children?.filter((child) => paths.includes(child.path)) ?? [];
    if (group?.children) {
      group.children = group.children.filter((child) => !paths.includes(child.path));
    }
    return selected;
  };
  const memberLevels = takeChildren('members', [
    '/admin/points-config',
    '/admin/association-promotion',
    '/admin/member-tags',
  ]);
  const emailSettings = takeChildren('system', ['/admin/email-settings']);
  const tournamentSettings = takeChildren('tournament', [
    '/tournament/blind-structures',
    '/tournament/timed-settlement-levels',
    '/tournament/timed-fee-plans',
    '/tournament/payout-rules',
    '/tournament/templates',
  ]);
  const insertSettings = (after: string, group: NavItem) => {
    if (group.children?.length) {
      items.splice(items.findIndex((item) => item.iconKey === after) + 1, 0, group);
    }
  };
  insertSettings('members', {
    label: t('tournament:feeSettings.memberSettings'),
    iconKey: 'memberSettings',
    icon: iconOverrides?.memberSettings || 'wallet',
    children: [
      ...memberLevels,
      ...WALLET_DESTINATIONS.map(({ section, label, iconKey, icon }) => ({
        label: t(label),
        path: `${MEMBER_WALLET_PATH}/${section}`,
        iconKey,
        icon: iconOverrides?.[iconKey] || iconOverrides?.memberWallet || icon,
      })),
    ],
  });
  insertSettings('accounting', {
    label: t('tournament:feeSettings.accountingSettings'),
    iconKey: 'accountingSettings',
    icon: iconOverrides?.accountingSettings || 'settings',
    children: emailSettings,
  });
  insertSettings('tournament', {
    label: t('tournament:feeSettings.tournamentSettings'),
    iconKey: 'tournamentSettings',
    icon: iconOverrides?.tournamentSettings || 'settings',
    children: tournamentSettings,
  });
  return items;
}

/** Regroup existing, already filtered destinations without introducing routes or grants. */
export function buildSidebarItems(
  items: NavItem[],
  t: (key: string) => string,
  iconOverrides?: Record<string, string>,
): NavItem[] {
  const operationKeys = ['tables', 'tournament', 'tvDisplay'];
  const settingKeys = ['memberSettings', 'accountingSettings', 'tournamentSettings'];
  const operations = items.filter((item) => operationKeys.includes(item.iconKey));
  const settings = items.filter((item) => settingKeys.includes(item.iconKey));
  const leaves = (item: NavItem) =>
    item.children ?? (item.path ? [{ ...item, path: item.path }] : []);
  return items.flatMap((item) => {
    if (operationKeys.includes(item.iconKey)) {
      return item !== operations[0]
        ? []
        : [
            {
              label: t('nav.operations'),
              iconKey: 'operations',
              icon: iconOverrides?.operations || operations[0].icon,
              children: operations.flatMap(leaves),
            },
          ];
    }
    if (settingKeys.includes(item.iconKey)) {
      return item !== settings.at(-1)
        ? []
        : [
            {
              label: t('nav.settingsCenter'),
              iconKey: 'settingsCenter',
              icon: iconOverrides?.settingsCenter || 'settings',
              children: settings.flatMap((group) =>
                leaves(group).map((child) => ({ ...child, section: group.label })),
              ),
            },
          ];
    }
    return [item];
  });
}

/** Page visibility is explicit; API actions remain separately enforced by the server. */
export function hasExactAssociationPageAccess(
  path: string,
  visiblePages: string[] | null,
): boolean {
  const normalized = path.replace(/\/+$/, '');
  const target = [
    PagePath.MEMBERS_BRAVE_RANKING,
    PagePath.ADMIN_ASSOCIATION_PROMOTION,
    PagePath.ADMIN_MEMBER_TAGS,
  ].find((page) => normalized === page || normalized.startsWith(`${page}/`));
  return !target || (normalized === target && visiblePages?.includes(target) === true);
}
